import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { PhoneInputWUT } from './phone-input/phone-input-wut';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
import { useError } from '../../field-error';
import { useFieldAttributes } from '../../services/field-attributes';
import { FieldDescriptionWUT } from '../field-description/field-description-wut';
import { FieldLabelWUT } from '../field-label/field-label-wut';
import { FieldErrorWUT } from '../field-error/field-error-wut';
import { classes } from './phone-input-field-wut.st.css';
import { useInvokeCallback } from '../shared/use-invoke-callback';
import { TextFieldTheme } from 'wix-ui-tpa/cssVars';
import { useSubmitSuccessEvent } from '../../contexts/events-context';
import { parseCountryCodeFromCallingCode, parseLocalPhone } from './phone-input/phone-input-utils';
import { useExperiments } from '../../contexts/experiments-context';
import { EXPERIMENTS } from '../../constants';
import { toE164 } from './format-phone-number';
import { useTranslation } from '../../contexts/translation-context';
export const PhoneInputFieldWUT = _ref => {
  var _validation$string;
  let {
    id,
    target,
    label,
    hideLabel = false,
    placeholder,
    required = false,
    description,
    value = '',
    defaultCountry,
    showFlag,
    validation,
    onChange: _onChange,
    onBlur,
    onFocus,
    error,
    disabled = false,
    customErrors,
    fieldType,
    FieldLayout,
    requiredIndicatorType,
    requiredIndicatorPlacement,
    readOnly,
    defaultValue
  } = _ref;
  const {
    t
  } = useTranslation();
  const {
    isExperimentEnabled
  } = useExperiments();
  const isLocalPhoneNumbersEnabled = isExperimentEnabled(EXPERIMENTS.LOCAL_PHONE_NUMBERS);
  const {
    labelId,
    inputId,
    errorId,
    descriptionId,
    ariaDescribedBy
  } = useFieldAttributes(id, error, description);
  const allowedCountryCodes = validation == null || (_validation$string = validation.string) == null || (_validation$string = _validation$string.phoneOptions) == null ? void 0 : _validation$string.allowedCountryCodes;
  const getInitialCurrentValue = () => {
    // TODO: remove this when removing isLocalPhoneNumbersEnabled
    if (defaultValue === value && defaultValue.length <= 4 && !!parseCountryCodeFromCallingCode(defaultValue) && isLocalPhoneNumbersEnabled) {
      return '';
    }
    return value;
  };
  const [currentValue, setCurrentValue] = useState(getInitialCurrentValue);
  const {
    hasError,
    errorMessage
  } = useError(error, customErrors, fieldType);
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const _value = isLocalPhoneNumbersEnabled ? currentValue : value;
  useEffect(() => {
    // TODO: remove when removing specs.forms.LocalPhoneNumbers
    if (currentValue !== value) {
      _onChange(currentValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const onChange = useCallback((newValue, selectedCountry) => {
    if (!isLocalPhoneNumbersEnabled) {
      _onChange(newValue);
      return;
    }
    const allowedCountry = (allowedCountryCodes == null ? void 0 : allowedCountryCodes.length) === 1 ? allowedCountryCodes[0] : undefined;
    const country = selectedCountry || allowedCountry;
    const parsedValue = parseLocalPhone(newValue, country);
    setCurrentValue(newValue);
    _onChange(parsedValue);
  }, [_onChange, allowedCountryCodes, isLocalPhoneNumbersEnabled]);
  const invokeBlur = useInvokeCallback(onBlur);
  const handleBlur = useCallback(() => {
    if (!isLocalPhoneNumbersEnabled) {
      const e164PhoneNumber = typeof value === 'string' && value ? toE164(value) : value;
      onChange(e164PhoneNumber);
    }
    invokeBlur();
  }, [invokeBlur, isLocalPhoneNumbersEnabled, onChange, value]);
  const onSubmitSuccess = useCallback(() => {
    if (isLocalPhoneNumbersEnabled) {
      setCurrentValue('');
      _onChange('');
    }
  }, [isLocalPhoneNumbersEnabled, _onChange]);
  useSubmitSuccessEvent(onSubmitSuccess);
  return /*#__PURE__*/React.createElement(FieldLayout, {
    fieldId: id,
    renderLabel: () => /*#__PURE__*/React.createElement(FieldLabelWUT, {
      labelId: labelId,
      htmlFor: inputId,
      required: required,
      label: label,
      hideLabel: hideLabel,
      requiredIndicatorType: requiredIndicatorType,
      requiredIndicatorPlacement: requiredIndicatorPlacement
    }),
    renderInput: () => /*#__PURE__*/React.createElement(PhoneInputWUT, {
      fieldId: id,
      inputId: inputId,
      hasError: hasError,
      value: _value ? _value : '',
      className: classNames(classes.textField, classes.theme, {
        [classes.error]: hasError,
        [classes.hover]: forcedHover
      }),
      onChange: onChange,
      required: required,
      disabled: disabled,
      placeholder: placeholder,
      onBlur: handleBlur,
      onFocus: onFocus,
      theme: TextFieldTheme.Box,
      ariaDescribedBy: ariaDescribedBy,
      ariaInvalid: hasError,
      showFlag: showFlag,
      defaultCountry: defaultCountry,
      allowedCountryCodes: allowedCountryCodes,
      readOnly: readOnly,
      ariaLabel: `${label}. ${t('field.phone.aria-label')}`
    }),
    renderDescription: () => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FieldErrorWUT, {
      target: target,
      hasError: hasError,
      errorMessage: errorMessage,
      id: errorId
    }), /*#__PURE__*/React.createElement(FieldDescriptionWUT, {
      description: description,
      id: descriptionId
    }))
  });
};
//# sourceMappingURL=phone-input-field-wut.js.map